import { randomId } from '@aha-app/mvc';
import { normalizeVerticalAlign } from '../utils/verticalAlign';
import type WhiteboardController from '../controllers/WhiteboardController';
import Shape from '../objects/Shape';
import { sanitizeTextForEditor } from './ToolPasteTextUtils';

// There are three easy ways to test these functions.
//   1) Override the htmlText parameter
//   2) Copy some HTML to your clipboard under the text/html format and paste it
//   3) Copy a table from an Aha! note and paste it
//
// For 1, you can simply override the htmlText parameter below with the HTML you
// want. Here is a sample table you can use.
//
// htmlText = `
//   <table style="border-collapse: collapse; width: 100%;">
//     <tr>
//       <th style="border: 1px solid black; padding: 15px; background-color: #f2f2f2; font-size: 16px;">Header 1</th>
//       <th width="50" style="border: 1px solid black; padding: 15px; background-color: #f2f2f2; font-size: 16px;">Header 2</th>
//       <th style="border: 1px solid black; padding: 15px; background-color: #f2f2f2; font-size: 16px;">Header 3</th>
//     </tr>
//     <tr>
//       <td style="border: 1px solid black; padding: 15px;">Short text</td>
//       <td width="50" style="border: 1px solid black; padding: 15px;">This cell has more content to test how the width adapts to longer text content. Every gesture here was a language: the tilt of a head, the pause before breath, the whisper of sand falling from a robe. Words were dangerous, for they left echoes that even the desert remembered.</td>
//       <td style="border: 1px solid black; padding: 15px; text-align: right;">Right aligned</td>
//       <td>This cell has more content and no cell width to test how the width adapts to longer text content. Every gesture here was a language: the tilt of a head, the pause before breath, the whisper of sand falling from a robe. Words were dangerous, for they left echoes that even the desert remembered.</td>
//     </tr>
//     <tr>
//       <td colspan="2" style="border: 1px solid black; padding: 15px;">This cell spans two columns to test colspan handling</td>
//       <td rowspan="2" style="border: 1px solid black; padding: 15px; vertical-align: middle;">This cell spans two rows to test rowspan handling</td>
//     </tr>
//     <tr>
//       <td style="border: 1px solid black; padding: 15px; background-color: #e6f7ff;">Colored cell</td>
//       <td style="border: 1px solid black; padding: 15px;">
//         <ul>
//           <li>List item 1</li>
//           <li>List item 2</li>
//         </ul>
//       </td>
//     </tr>
//     <tr>
//       <td style="border: 1px solid black; padding: 15px; height: 100px"></td>
//       <td style="border: 1px solid black; padding: 15px;"></td>
//       <td style="border: 1px solid black; padding: 15px;"></td>
//     </tr>
//   </table>
// `;
//
// For 2, copy the html you want to the clipboard. Then run the following in a
// js console:
//
// const html = '<paste>';
// navigator.clipboard.write([new ClipboardItem({ "text/html": new Blob([html], { type: "text/html" }), "text/plain": new Blob([html], { type: "text/plain" })})]);

/**
 * Parses the provided HTML text, extracts any tables, and builds whiteboard
 * JSON objects for each table found. The tables are positioned starting at
 * (placeAtX, placeAtY) and stacked vertically with a small gap.
 *
 * @param htmlText The HTML text to parse for tables
 * @param placeAtX The X coordinate to place the first table
 * @param placeAtY The Y coordinate to place the first table
 * @param placeAtZ The Z index to assign to the tables
 * @returns An object mapping table IDs to their corresponding whiteboard JSON configurations
 */
export async function buildWhiteboardsJSONFromHTML(
  whiteboardController: WhiteboardController,
  htmlText,
  placeAtX,
  placeAtY,
  placeAtZ
) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlText, 'text/html');

  let nextY = placeAtY;
  const whiteboardObjects = {};
  const children = Array.from(doc.body.children);

  // Check if ALL non-empty elements are list-related (ul, ol, or li)
  // Only convert to stickies if pasting ONLY lists
  const isListElement = (el: Element) =>
    el instanceof HTMLUListElement ||
    el instanceof HTMLOListElement ||
    el instanceof HTMLLIElement;

  const nonEmptyChildren = children.filter(
    child => child.textContent && child.textContent.trim() !== ''
  );

  const allElementsAreLists = nonEmptyChildren.every(isListElement);

  if (allElementsAreLists && nonEmptyChildren.length > 0) {
    // Only lists - convert to stickies
    const allListItems: HTMLLIElement[] = [];

    for (const child of children) {
      if (
        child instanceof HTMLUListElement ||
        child instanceof HTMLOListElement
      ) {
        allListItems.push(...child.querySelectorAll('li'));
      } else if (child instanceof HTMLLIElement) {
        allListItems.push(child);
      }
    }

    if (allListItems.length > 0) {
      const result = await processListItems(
        allListItems,
        placeAtX,
        nextY,
        placeAtZ
      );

      if (result) {
        Object.assign(whiteboardObjects, result.objects);
        nextY = result.nextY;
      }
    }
  } else {
    // Mixed content or no lists - process normally
    let i = 0;
    while (i < children.length) {
      const element = children[i] as HTMLElement;

      if (
        element instanceof HTMLTableElement ||
        element.querySelector('table')
      ) {
        const result = await processElement(
          whiteboardController,
          element,
          placeAtX,
          nextY,
          placeAtZ
        );

        if (result) {
          Object.assign(whiteboardObjects, result.objects);
          nextY = result.nextY;
        }
        i++;
      } else {
        const textElements: HTMLElement[] = [];
        // Pulls all consecutive text elements together
        while (
          i < children.length &&
          !(children[i] instanceof HTMLTableElement) &&
          !children[i].querySelector('table')
        ) {
          textElements.push(children[i] as HTMLElement);
          i++;
        }

        if (textElements.length > 0) {
          const result = await processTextElements(
            whiteboardController,
            textElements,
            placeAtX,
            nextY,
            placeAtZ
          );

          if (result) {
            Object.assign(whiteboardObjects, result.objects);
            nextY = result.nextY;
          }
        }
      }
    }
  }

  return whiteboardObjects;
}

/**
 * Processes list items and creates sticky notes for each one.
 * Only called when pasting ONLY lists (no mixed content).
 *
 * @param listItems Array of HTML list item elements
 * @param placeAtX The X coordinate to place the notes
 * @param nextY The Y coordinate to place the notes
 * @param placeAtZ The Z index for the notes
 * @returns An object containing the created note objects and the updated Y position
 */
async function processListItems(
  listItems: HTMLLIElement[],
  placeAtX: number,
  nextY: number,
  placeAtZ: number
): Promise<{ objects: Record<string, Partial<Shape>>; nextY: number } | null> {
  if (listItems.length === 0) {
    return null;
  }

  const objects: Record<string, Partial<Shape>> = {};
  const STICKY_WIDTH = 100;
  const STICKY_HEIGHT = 100;
  const HORIZONTAL_SPACING = 20;
  const VERTICAL_SPACING = 20;
  const NOTES_PER_ROW = 4;

  let currentX = placeAtX;
  let currentY = nextY;
  let maxHeightInRow = 0;

  for (let index = 0; index < listItems.length; index++) {
    const item = listItems[index];
    const textContent = sanitizeTextForEditor(
      item.innerHTML || `<p>${item.textContent || ''}</p>`
    );

    const noteId = randomId() as RecordId;

    const noteConfig: Partial<Shape> = {
      id: noteId,
      type: 'shape',
      shapeName: 'note',
      z: placeAtZ,
      left: currentX,
      top: currentY,
      width: STICKY_WIDTH,
      height: STICKY_HEIGHT,
      scaleX: 1,
      scaleY: 1,
      textContent,
      fill: '#FAEBB9',
      strokeType: 0,
      strokeWidth: 0,
      opacity: 1,
      fontSize: 14,
      angle: 0,
    };

    objects[noteId] = noteConfig;

    maxHeightInRow = Math.max(maxHeightInRow, STICKY_HEIGHT);

    if ((index + 1) % NOTES_PER_ROW === 0) {
      currentX = placeAtX;
      currentY += maxHeightInRow + VERTICAL_SPACING;
      maxHeightInRow = 0;
    } else {
      currentX += STICKY_WIDTH + HORIZONTAL_SPACING;
    }
  }

  const finalY =
    maxHeightInRow > 0
      ? currentY + maxHeightInRow + VERTICAL_SPACING
      : currentY;

  return { objects, nextY: finalY };
}

/**
 * Processes multiple consecutive text elements and combines them into a single text box.
 *
 * @param whiteboardController The whiteboard controller
 * @param elements The HTML elements to combine
 * @param placeAtX The X coordinate to place the text box
 * @param nextY The Y coordinate to place the text box
 * @param placeAtZ The Z index for the text box
 * @returns An object containing the created whiteboard object and the updated Y position
 */
async function processTextElements(
  whiteboardController: WhiteboardController,
  elements: HTMLElement[],
  placeAtX: number,
  placeAtY: number,
  placeAtZ: number
): Promise<{ objects: Record<string, any>; nextY: number } | null> {
  const nonEmptyElements = elements.filter(
    el => el.textContent && el.textContent.trim() !== ''
  );

  if (nonEmptyElements.length === 0) {
    return null;
  }

  const combinedHTML = nonEmptyElements
    .map(el => el.outerHTML || `<p>${el.textContent || ''}</p>`)
    .join('<p><br></p>');

  const textContent = sanitizeTextForEditor(combinedHTML);

  const textConfig: any = {
    id: randomId(),
    type: 'shape',
    shapeName: 'text',
    z: placeAtZ,
    left: placeAtX,
    top: placeAtY,
    scaleX: 1,
    scaleY: 1,
    textContent,
    fontSize: 14,
    textAlign: 'left',
    width: 400,
    wrapText: true,
  };

  const measureShape = new Shape({
    controller: whiteboardController,
    shapeName: 'text',
  });

  measureShape.set(textConfig);
  const dimensions = await measureShape.measureTextDimensions();

  textConfig.height = Math.max(dimensions.height, 20);

  if (!isFinite(textConfig.height)) {
    return null;
  }

  return {
    objects: { [textConfig.id]: textConfig },
    nextY: placeAtY + textConfig.height + 20,
  };
}

/**
 * Recursively processes an HTML element and its children, creating whiteboard objects
 * for tables and text content while preserving the original order.
 *
 * @param whiteboardController The whiteboard controller
 * @param element The HTML element to process
 * @param placeAtX The X coordinate to place objects
 * @param nextY The Y coordinate to place objects
 * @param placeAtZ The Z index for objects
 * @returns An object containing the created whiteboard objects and the updated Y position
 */
async function processElement(
  whiteboardController: WhiteboardController,
  element: HTMLElement,
  placeAtX: number,
  nextY: number,
  placeAtZ: number
): Promise<{ objects: Record<string, any>; nextY: number } | null> {
  // Skip empty elements
  if (!element.textContent || element.textContent.trim() === '') {
    return null;
  }

  const objects = {};

  // Check if this element is a table
  if (element instanceof HTMLTableElement) {
    const tableConfig = buildWhiteboardJSONForTable(
      element,
      placeAtX,
      nextY,
      placeAtZ
    );

    if (tableConfig) {
      objects[tableConfig.id] = tableConfig;
      nextY += tableConfig.height + 20;
    }

    return { objects, nextY };
  }

  // Check if this element contains a table
  const containsTable = element.querySelector('table') !== null;

  if (containsTable) {
    // Process each child element to maintain order
    for (const childElement of Array.from(element.children)) {
      const result = await processElement(
        whiteboardController,
        childElement as HTMLElement,
        placeAtX,
        nextY,
        placeAtZ
      );

      if (result) {
        Object.assign(objects, result.objects);
        nextY = result.nextY;
      }
    }

    return { objects, nextY };
  }

  // This is a text element without tables
  const textConfig = buildWhiteboardJSONForTextElement(
    element,
    placeAtX,
    nextY,
    placeAtZ
  );

  if (!textConfig) {
    return null;
  }

  textConfig.width = 400;
  textConfig.wrapText = true;

  const measureShape = new Shape({
    controller: whiteboardController,
    shapeName: 'text',
  });

  measureShape.set(textConfig);
  const dimensions = await measureShape.measureTextDimensions();

  textConfig.height = Math.max(dimensions.height, 20);

  if (isFinite(textConfig.height)) {
    objects[textConfig.id] = textConfig;
    nextY += textConfig.height + 20;
  }

  return { objects, nextY };
}

/**
 * Builds a whiteboard JSON configuration for a single HTML table element.
 */
function buildWhiteboardJSONForTable(
  table: HTMLTableElement,
  placeAtX: number,
  placeAtY: number,
  placeAtZ: number
) {
  const rows = Array.from(table.querySelectorAll('tr'));

  if (rows.length === 0) {
    return;
  }

  const cellMatrix = calculateCellMatrix(rows);

  const effectiveNumRows = cellMatrix.filter(row =>
    row.some(cell => cell)
  ).length;

  const effectiveNumColumns = cellMatrix.reduce(
    (max, row) => Math.max(max, row.length),
    0
  );

  // Measure the dimensions of the table
  const { colWidths, rowHeights } = measureTableDimensions(table);

  const tableConfig: any = {
    id: randomId(),
    type: 'table',
    z: placeAtZ,
    textContent: '', // No title for the table
    left: placeAtX,
    top: placeAtY,
    width: colWidths.reduce((total, width) => total + width, 0),
    height: rowHeights.reduce((total, height) => total + height, 0),
    scaleX: 1,
    scaleY: 1,
    numColumns: effectiveNumColumns,
    numRows: effectiveNumRows,
    textFields: {},
  };

  // Set column widths
  colWidths.forEach((width, index) => {
    tableConfig[`col_${index}_width`] = width;
  });

  // Set row heights
  rowHeights.forEach((height, index) => {
    tableConfig[`row_${index}_height`] = height;
  });

  let rowIndex = 0;

  rows.forEach(row => {
    const cells = Array.from(row.querySelectorAll('td,th'));

    let colIndex = 0;

    cells.forEach((cell: HTMLTableCellElement) => {
      applyJsonForCell(cell, tableConfig, rowIndex, colIndex);
      colIndex += cellMatrix[rowIndex][colIndex];
    });

    rowIndex += 1;

    while (cellMatrix[rowIndex]?.[0] === -1) {
      rowIndex += 1;
    }
  });

  return tableConfig;
}

/**
 * Calculates the grid layout and effective grid size accounting
 * for rowspan and colspan. Each grid location will record either how many
 * columns it spans, or that it's part of some other merged cell.
 */
function calculateCellMatrix(rows: HTMLTableRowElement[]) {
  const cellMatrix: number[][] = [];

  rows.forEach((row, rowIndex) => {
    const cells = Array.from(row.querySelectorAll('td,th'));
    let colIndex = 0;

    cells.forEach(cell => {
      // Find the next available column position
      while (cellMatrix[rowIndex]?.[colIndex]) {
        colIndex++;
      }

      const colSpan = parseInt(cell.getAttribute('colspan') || '1', 10);
      const rowSpan = parseInt(cell.getAttribute('rowspan') || '1', 10);

      // Mark cells as occupied in the matrix
      for (let r = 0; r < rowSpan; r++) {
        for (let c = 0; c < colSpan; c++) {
          cellMatrix[rowIndex + r] ||= [];
          // -1 signifies it's part of a merged cell. The parent
          // will have the colSpan saved on it.
          cellMatrix[rowIndex + r][colIndex + c] = -1;
        }
      }

      cellMatrix[rowIndex] ||= [];
      cellMatrix[rowIndex][colIndex] = colSpan;

      colIndex += colSpan;
    });
  });

  return cellMatrix;
}

/**
 * Measures the dimensions of an HTML table and returns arrays of column widths
 * and row heights. It does this by rendering a hidden copy of the table in the
 * DOM and inspecting the sizes of each cell.
 *
 * @param tableElement The HTML table element to measure
 * @returns An object containing arrays of column widths and row heights
 */
function measureTableDimensions(tableElement: HTMLTableElement): {
  colWidths: number[];
  rowHeights: number[];
} {
  // Create a temporary container to render the table for measurement
  const container = document.createElement('div');
  container.id = 'table-measurement-container';
  container.style.position = 'absolute';
  container.style.visibility = 'hidden';
  container.style.left = '-9999px';
  container.style.top = '-9999px';
  container.style.width = 'auto';
  container.style.height = 'auto';

  // Clone the table to avoid modifying the original
  const tableClone = tableElement.cloneNode(true) as HTMLTableElement;

  // Make sure the table is rendered with its natural dimensions
  tableClone.style.width = 'auto';
  tableClone.style.height = 'auto';
  tableClone.style.tableLayout = 'auto';

  container.appendChild(tableClone);
  document.body.appendChild(container);

  // Initialize arrays with minimum dimensions
  const MIN_COLUMN_WIDTH = 50;
  const MIN_ROW_HEIGHT = 50;
  const colWidths: number[] = [];
  const rowHeights: number[] = [];

  try {
    // Get all rows in the table
    const rows = tableClone.querySelectorAll('tr');

    rows.forEach((row, rowIndex) => {
      const cells = row.querySelectorAll('td, th');
      let colIndex = 0;
      cells.forEach(cell => {
        // Get cell dimensions
        const rect = cell.getBoundingClientRect();
        const width = rect.width;
        const height = rect.height;

        // Handle colspan - distribute width proportionally
        const colSpan = parseInt(cell.getAttribute('colspan') ?? '1', 10);
        if (colSpan === 1) {
          colWidths[colIndex] = Math.max(colWidths[colIndex] ?? 0, width);
        } else {
          // For cells with colspan, distribute width proportionally
          // We'll use a simple approach of dividing equally for now
          const widthPerCol = width / colSpan;
          for (let i = 0; i < colSpan; i++) {
            colWidths[colIndex + i] = Math.max(
              colWidths[colIndex + i] ?? 0,
              widthPerCol
            );
          }
        }

        // Handle rowspan - distribute height proportionally
        const rowSpan = parseInt(cell.getAttribute('rowspan') ?? '1', 10);
        if (rowSpan === 1) {
          rowHeights[rowIndex] = Math.max(rowHeights[rowIndex] ?? 0, height);
        } else {
          // For cells with rowspan, distribute height proportionally
          const heightPerRow = height / rowSpan;
          for (let i = 0; i < rowSpan; i++) {
            rowHeights[rowIndex + i] = Math.max(
              rowHeights[rowIndex + i] ?? 0,
              heightPerRow
            );
          }
        }

        colIndex += colSpan;
      });
    });

    // Round dimensions to integers
    // Add an extra 15% width to columns to account for differences in padding
    // and text rendering. Erring on the larger side helps ensure we don't
    // have odd word wrapping.
    return {
      colWidths: colWidths.map(width =>
        Math.min(Math.max(Math.round(width * 1.15), MIN_COLUMN_WIDTH), 500)
      ),
      rowHeights: rowHeights.map(height =>
        Math.max(Math.round(height), MIN_ROW_HEIGHT)
      ),
    };
  } finally {
    // Clean up - remove the temporary container
    document.body.removeChild(container);
  }
}

/**
 * Calculates all the necessary attributes for the given html cell and adds them
 * to the tableConfig.
 *
 * @param cell The HTML table cell element (td or th)
 * @param tableConfig The whiteboards json for the table to update
 * @param row The row index of the cell
 * @param col The column index of the cell
 */
function applyJsonForCell(
  cell: HTMLTableCellElement,
  tableConfig: { textFields: Record<string, any> },
  row: number,
  col: number
) {
  const cellKey = `cell_${row}_${col}`;

  const cellTextJson = jsonForCellText(cell);
  tableConfig.textFields[cellKey] = cellTextJson;

  // Handle merged cells
  const colspan = parseInt(cell.getAttribute('colspan') || '1', 10);
  const rowspan = parseInt(cell.getAttribute('rowspan') || '1', 10);

  if (colspan > 1 || rowspan > 1) {
    const mergedCellKey = `merged_cell_${row}_${col}_${row + rowspan - 1}_${
      col + colspan - 1
    }`;
    tableConfig[mergedCellKey] = true;
  }

  const bgColor = cell.style.backgroundColor || '#FFFFFF';
  if (bgColor !== '#FFFFFF' && bgColor !== 'transparent') {
    tableConfig[`${cellKey}_fill`] = bgColor;
  }
}

/**
 * Constructxs the textContent portion of a particular cell in a whiteboards
 * table json.
 *
 * @param cell The HTML table cell element (td or th)
 * @returns An object containing the text content and styling for the cell
 */
function jsonForCellText(cell: HTMLTableCellElement) {
  // Extract cell content
  let textContent = '';
  if (cell.innerHTML) {
    // Preserve HTML formatting
    textContent = cell.innerHTML;
  } else {
    textContent = `<p>${cell.textContent || ''}</p>`;
  }

  // Use the editor serializer to clean up the HTML. If we don't do this
  // now, it will be done the first time the cell is clicked, which can
  // cause the content to shift unexpectedly.
  textContent = sanitizeTextForEditor(textContent);

  // Handle text alignment
  let textAlign = 'left';
  if (cell.style.textAlign === 'center') {
    textAlign = 'center';
  } else if (cell.style.textAlign === 'right') {
    textAlign = 'right';
  }

  const verticalAlign = normalizeVerticalAlign(cell.style.verticalAlign);

  return {
    fontSize: 14,
    textAlign,
    verticalAlign,
    textContent,
  };
}

/**
 * Builds a whiteboard JSON configuration for a text element (paragraph, heading, list, etc.).
 *
 * @param element The HTML element to convert to a whiteboard text object
 * @param placeAtX The X coordinate to place the text object
 * @param placeAtY The Y coordinate to place the text object
 * @param placeAtZ The Z index to assign to the text object
 * @returns A whiteboard JSON configuration for the text element
 */
function buildWhiteboardJSONForTextElement(
  element: HTMLElement,
  placeAtX: number,
  placeAtY: number,
  placeAtZ: number
) {
  // Skip empty elements
  if (!element.textContent || element.textContent.trim() === '') {
    return null;
  }

  // Extract text content
  let textContent = '';
  if (element.innerHTML) {
    // Preserve HTML formatting
    textContent = element.innerHTML;
  } else {
    textContent = `<p>${element.textContent || ''}</p>`;
  }

  textContent = sanitizeTextForEditor(textContent);

  // Determine text alignment
  let textAlign = 'left';
  const computedStyle = window.getComputedStyle(element);
  if (computedStyle.textAlign === 'center') {
    textAlign = 'center';
  } else if (computedStyle.textAlign === 'right') {
    textAlign = 'right';
  }

  // Create the text object configuration
  const textConfig: any = {
    id: randomId(),
    type: 'shape',
    shapeName: 'text',
    z: placeAtZ,
    left: placeAtX,
    top: placeAtY,
    scaleX: 1,
    scaleY: 1,
    textContent,
    fontSize: 14,
    textAlign,
  };

  return textConfig;
}
